import { SEARCH_SPLIT_REGEX } from '~/chords/Select/components/DropdownTreeSingleSelect/constants';
import type { InternalDropdownOption } from '~/chords/Select/components/DropdownTreeSingleSelect/types';

/**
 * Recursively update isVisible=true|false on the options array based on search terms.
 * @param options - The options to update.
 * @param param1.searchQueryTrimmed - The trimmed search query.
 * @returns The updated options.
 */
export function updateIsVisibleSearchRecursive(
  options: InternalDropdownOption[],
  { searchQueryTrimmed }: { searchQueryTrimmed: string },
): InternalDropdownOption[] {
  const searchQueryLower = searchQueryTrimmed.toLowerCase();
  const searchTerms = searchQueryLower.split(SEARCH_SPLIT_REGEX);

  return options.map((option): InternalDropdownOption => {
    const optionLabel = option.label || option.value;
    const optionLabelWords = optionLabel
      .toLowerCase()
      .split(SEARCH_SPLIT_REGEX);
    const isMatched = searchTerms.some((term) =>
      optionLabelWords.some((word) => word.startsWith(term)),
    );

    let children: InternalDropdownOption[] | undefined;

    if (option.children) {
      children = updateIsVisibleSearchRecursive(option.children, {
        searchQueryTrimmed,
      });
    }

    // Option isVisible if it matches or any descendant isVisible.
    const hasVisibleDescendant = (
      childOptions?: InternalDropdownOption[],
    ): boolean =>
      childOptions?.some(
        (childOption) =>
          childOption.isVisible || hasVisibleDescendant(childOption.children),
      ) ?? false;

    const isVisible = isMatched || hasVisibleDescendant(children);

    return {
      ...option,
      isVisible,
      isExpanded: isVisible,
      children,
    };
  });
}
